import type { ReactNode } from "react";
import { StatusPill, type StatusPillTone } from "../app-shell";

export type WorkflowProgressJsonValue =
  | null
  | boolean
  | number
  | string
  | WorkflowProgressJsonValue[]
  | { [key: string]: WorkflowProgressJsonValue };

export type WorkflowProgressValue = {
  key: string;
  label: string;
  value: WorkflowProgressJsonValue;
  unit?: string;
  type?: string;
};

export type WorkflowProgressItem = {
  label: string;
  status: string;
  values: WorkflowProgressValue[];
  updatedAt: string | null;
};

export type WorkflowProgressSummaryProps = {
  title?: ReactNode;
  coverage: "ready" | "warming";
  items: readonly WorkflowProgressItem[];
  loading?: boolean;
  error?: boolean;
  unavailable?: boolean;
  onRetry?: () => void;
};

function statusTone(status: string): StatusPillTone {
  switch (status.toLowerCase()) {
    case "completed":
    case "succeeded":
      return "success";
    case "failed":
    case "cancelled":
    case "error":
      return "error";
    case "running":
    case "pending":
    case "queued":
      return "info";
    default:
      return "neutral";
  }
}

function formatValue(value: WorkflowProgressJsonValue): string {
  if (typeof value === "string") return value;
  if (value === null) return "null";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function coverageTone(coverage: "ready" | "warming", unavailable: boolean): StatusPillTone {
  if (unavailable) return "neutral";
  return coverage === "ready" ? "success" : "warning";
}

function coverageLabel(coverage: "ready" | "warming", unavailable: boolean): string {
  if (unavailable) return "Unavailable";
  return coverage === "ready" ? "Ready" : "Warming";
}

export function WorkflowProgressSummary({
  title = "Workflow progress",
  coverage,
  items,
  loading = false,
  error = false,
  unavailable = false,
  onRetry,
}: WorkflowProgressSummaryProps) {
  const label = coverageLabel(coverage, unavailable);
  const tone = coverageTone(coverage, unavailable);

  return (
    <section aria-label="Workflow progress" className="border-t border-[var(--border-soft)] pt-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--nzr-muted)]">{title}</h3>
        <StatusPill tone={tone}>{label}</StatusPill>
      </div>
      {loading && items.length === 0 && (
        <p className="text-[12px] text-[var(--nzr-muted)]">Progress is loading…</p>
      )}
      {error && (
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-[var(--red)]">
          <span>{items.length > 0 ? "Refresh failed; showing the last known progress." : "Workflow progress is unavailable."}</span>
          {onRetry && <button type="button" className="underline underline-offset-2" onClick={onRetry} disabled={loading}>Retry</button>}
        </div>
      )}
      {items.length > 0 && (
        <div className="flex flex-col gap-2">
          {items.map((item) => (
            <div key={item.label} className="rounded-sm border border-[var(--nzr-border)] bg-[var(--nzr-card)] px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="min-w-0 truncate text-[12px] font-medium text-[var(--ink)]">{item.label}</span>
                <div className="flex items-center gap-2">
                  <StatusPill tone={statusTone(item.status)}>{item.status}</StatusPill>
                  {item.updatedAt && <span className="text-[11px] text-[var(--nzr-muted)]">{item.updatedAt}</span>}
                </div>
              </div>
              {item.values.length > 0 && (
                <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[var(--nzr-muted)]">
                  {item.values.map((value) => (
                    <div key={value.key} className="flex items-baseline gap-1">
                      <dt className="font-medium text-[var(--ink)]">{value.label}</dt>
                      <dd className="break-words">
                        <span className="font-mono">{formatValue(value.value)}</span>
                        {value.unit ? ` (${value.unit})` : ""}
                        {value.type ? <span className="ml-1 text-[10px] opacity-75">[{value.type}]</span> : null}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
